import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeepPartial, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { array, InferType, object } from 'yup';

import {
  LegalPersonReadModel,
  PersonReadModel,
  useGetAgreementsByIdQuery,
  usePutAgreementsByAgreementIdPartiesMutation,
  useLazyGetAgreementsByAgreementIdPartiesAndPartyIdAvailableContactsQuery,
  useLazyGetTenantsByTenantIdPersonsQuery,
  SetAgreementPartyPayload,
  TenantReadModel,
  PermissionKeys,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  FormSubmitButton,
  InfoTooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  useFormAdd,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { AgreementPartyItem } from './agreement-party-item.component';
import { AgreementPartyFormData, AgreementPartyItemForm, agreementPartySchema } from './agreement-party-item.form';

import { CancelRequestModal, LegalPersonsAddModal } from '../../../../../components';
import { HasAgreementPermission } from '../../../../../components/permission/has-agreement-permission';

const schema = object({
  parties: array().of(agreementPartySchema).required(),
});

type FormValues = InferType<typeof schema>;
type FormDefaultValues = DeepPartial<FormValues>;

export type AgreementProductsFormData = FormValues;

export interface AgreementPartiesCardProps {
  className?: string;
  ownersManagers?: LegalPersonReadModel[];
}

export function AgreementPartiesCard({ className, ownersManagers }: AgreementPartiesCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tAgreements } = useTranslation('agreements');

  const { id } = useParams<{ id: string }>();
  const [editMode, setEditMode] = useState<'full' | 'contactOnly' | null>(null);
  const editing = editMode !== null;
  const isContactOnlyMode = editMode === 'contactOnly';

  const { data: agreement, isFetching, isLoading } = useGetAgreementsByIdQuery({ id: id ?? '' }, { skip: id == null });

  const isAgreementActive = agreement?.status === 'Effective';
  const isTerminated = agreement?.status === 'Terminated';

  const [putAgreementParties] = usePutAgreementsByAgreementIdPartiesMutation();

  const agreementParties = useMemo(() => agreement?.parties ?? [], [agreement?.parties]);

  // Separate owners/managers from tenants, with stable sort order
  const roleOrder: Record<string, number> = { Owner: 0, Manager: 1, Tenant: 2 };

  const ownersManagersParties = useMemo(
    () =>
      agreementParties
        .filter((party) => party.type === 'Owner' || party.type === 'Manager')
        .sort((a, b) => (roleOrder[a.type ?? ''] ?? 99) - (roleOrder[b.type ?? ''] ?? 99) || a.stakeholder.name.localeCompare(b.stakeholder.name)),
    [agreementParties],
  );

  const tenantParties = useMemo(
    () =>
      agreementParties
        .filter((party) => party.type === 'Tenant')
        .sort((a, b) => a.stakeholder.name.localeCompare(b.stakeholder.name)),
    [agreementParties],
  );

  // Available contacts per party (keyed by party id)
  const [availableContactsMap, setAvailableContactsMap] = useState<Record<string, PersonReadModel[]>>({});
  const [representativeMap, setRepresentativeMap] = useState<Record<string, string | null>>({});
  const [initialRepresentativeMap, setInitialRepresentativeMap] = useState<Record<string, string | null>>({});
  const [fetchAvailableContacts] = useLazyGetAgreementsByAgreementIdPartiesAndPartyIdAvailableContactsQuery();
  const [fetchTenantPersons] = useLazyGetTenantsByTenantIdPersonsQuery();

  // Load available contacts for all parties when entering edit mode
  useEffect(() => {
    if (!editing || !id || !agreement) return;

    const loadContacts = async () => {
      const contactsMap: Record<string, PersonReadModel[]> = {};
      const repMap: Record<string, string | null> = {};

      for (const party of agreementParties) {
        try {
          const result = await fetchAvailableContacts({ agreementId: id, partyId: party.id }).unwrap();
          contactsMap[party.id] = result;
        } catch {
          contactsMap[party.id] = [];
        }
        repMap[party.id] = party.representative?.id ?? null;
      }

      setAvailableContactsMap(contactsMap);
      setRepresentativeMap(repMap);
      setInitialRepresentativeMap({ ...repMap });
    };

    loadContacts();
  }, [editing, id, agreement]);

  const handleRepresentativeChange = useCallback((partyId: string, representativeId: string | null) => {
    setRepresentativeMap((prev) => ({ ...prev, [partyId]: representativeId }));
  }, []);

  const isRepresentativeDirty = useMemo(() => {
    return Object.keys(representativeMap).some(
      (key) => representativeMap[key] !== initialRepresentativeMap[key],
    );
  }, [representativeMap, initialRepresentativeMap]);

  const onSubmit = async (data: FormValues) => {
    if (id == null) return;

    try {
      const tenantPayloads: SetAgreementPartyPayload[] = data.parties.map<SetAgreementPartyPayload>((party) => ({
        id: party.id ?? null,
        stakeholderId: party.stakeholderId,
        role: party.role ?? 'Tenant',
        representativeId: representativeMap[party.id ?? party.stakeholderId] ?? null,
      }));

      const ownersManagersPayloads: SetAgreementPartyPayload[] = ownersManagersParties
        .filter((party) => party.type != null)
        .map<SetAgreementPartyPayload>((party) => ({
          id: party.id ?? null,
          stakeholderId: party.stakeholder.id,
          role: party.type!,
          representativeId: representativeMap[party.id] ?? party.representative?.id ?? null,
        }));

      await putAgreementParties({
        agreementId: id,
        body: [...ownersManagersPayloads, ...tenantPayloads],
      }).unwrap();
    } catch (e) {
      return;
    }

    setEditMode(null);
  };

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      parties: tenantParties.map<AgreementPartyFormData>((data) => ({
        id: data.id ?? undefined,
        stakeholderId: data.stakeholder.id ?? '',
        role: data.type ?? 'Tenant',
        representativeId: data.representative?.id ?? null,
      })),
    }),
    [tenantParties],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
    control,
  } = methods;

  const isAnyDirty = isDirty || isRepresentativeDirty;

  /**
   * #########################################
   * Edit Agreement Parties
   */

  const {
    fields: editedAgreementParties,
    items: stakeholders,
    setItems: setStakeholders,
    adding: addingLegalPersons,
    enableAdding: handleOpenAddLegalPersons,
    disableAdding: handleCloseAddLegalPersons,
    setNewItems: handleAddLegalPersons,
    removeItemByFieldIndex: handleRemoveLegalPerson,
    getItemByFieldIndex: getStakeholderByFieldIndex,
    getFormItemByFieldIndex: getFormItemByFieldIndex,
  } = useFormAdd<AgreementPartyFormData, TenantReadModel>({
    control,
    name: 'parties',
    mapItemToFormItem: (stakeholder): AgreementPartyFormData => {
      return {
        stakeholderId: stakeholder.id,
        role: 'Tenant',
      };
    },
    itemEqualsFormItem: (stakeholder, formItem) => stakeholder.id === formItem.stakeholderId,
    formItemEqualsFormItem: (x, y) => x.stakeholderId === y.stakeholderId,
  });

  // Fetch contacts for new parties (no partyId) using stakeholder/tenant ID
  const fetchingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!editing) return;
    const formValues = methods.getValues();
    const parties = formValues.parties ?? [];
    for (const party of parties) {
      const stakeholderId = party.stakeholderId;
      if (!party.id && stakeholderId && !availableContactsMap[stakeholderId] && !fetchingRef.current.has(stakeholderId)) {
        fetchingRef.current.add(stakeholderId);
        fetchTenantPersons({ tenantId: stakeholderId }).unwrap()
          .then((result) => setAvailableContactsMap((prev) => ({ ...prev, [stakeholderId]: result })))
          .catch(() => setAvailableContactsMap((prev) => ({ ...prev, [stakeholderId]: [] })))
          .finally(() => fetchingRef.current.delete(stakeholderId));
      }
    }
  }, [editing, editedAgreementParties]);

  useEffect(() => {
    const tenants = tenantParties.map((x) => x.stakeholder).filter((x) => x !== undefined);
    setStakeholders(tenants);
    reset(defaultValues);
  }, [defaultValues, editing]);

  const hasTenant = useMemo(() => {
    if (editing) {
      return editedAgreementParties != null && editedAgreementParties.length > 0;
    }
    return tenantParties.length > 0;
  }, [editing, editedAgreementParties, tenantParties]);

  const isListEmpty = useMemo(() => {
    const totalParties =
      ownersManagersParties.length + (editing ? editedAgreementParties.length : tenantParties.length);
    return totalParties === 0;
  }, [editing, ownersManagersParties.length, editedAgreementParties.length, tenantParties.length]);

  const handleOpenEdit = () => {
    setEditMode('full');
  };

  const handleOpenContactOnlyEdit = () => {
    setEditMode('contactOnly');
  };

  const [cancelingRequest, setCancelingRequest] = useState<boolean>(false);

  const checkCancelRequest = () => {
    if (isAnyDirty) {
      setCancelingRequest(true);
    } else {
      setEditMode(null);
    }
  };

  const handleConfirmCancelRequest = () => {
    setEditMode(null);
    setCancelingRequest(false);
  };

  const handleCancelCancelRequest = () => {
    setCancelingRequest(false);
  };

  return (
    <>
      <Card className={className}>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <CardHeader>
              <CardTitle className="flex-1" variant="highlighted">
                {tGlobal('entity.agreementParty', { context: 'plural' })}
              </CardTitle>
              <HasAgreementPermission id={id} permissions={[PermissionKeys.Agreement.Update]}>
                <CardActions>
                  {!editing ? (
                    <>
                      <InfoTooltip 
                        message={isTerminated ? tAgreements('terminatedAgreementEditDisabled') : tAgreements('activeAgreementEditDisabled')} 
                        disabled={!isAgreementActive && !isTerminated}
                        inline
                      >
                        <span>
                          <Button
                            color="primary"
                            disabledVariant="dark"
                            size="sm"
                            onClick={handleOpenEdit}
                            disabled={isFetching || id == null || agreement == null || isAgreementActive || isTerminated}
                          >
                            {tCommon('edit')}
                          </Button>
                        </span>
                      </InfoTooltip>
                      {isAgreementActive && !isTerminated && (
                        <Button
                          color="primary"
                          size="sm"
                          onClick={handleOpenContactOnlyEdit}
                          disabled={isFetching || id == null || agreement == null}
                        >
                          {tAgreements('agreement.editContactPerson')}
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button color="primary-dark" size="sm" onClick={checkCancelRequest}>
                        {tCommon('cancel')}
                      </Button>
                      <FormSubmitButton type="submit" size="sm" disabled={isFetching} disableDirtyHandling={isRepresentativeDirty}>
                        {tCommon('save')}
                      </FormSubmitButton>
                    </>
                  )}
                </CardActions>
              </HasAgreementPermission>
            </CardHeader>

            <CardContent variant="fill" className="justify-self-center rounded-lg w-full px-4 pb-4">
              <Table className="overflow-hidden w-full mb-4 rounded-md" size="xs" layout="fixed">
                {!isLoading && isListEmpty && !editing && (
                  <caption className="px-4 py-4 [caption-side:bottom]">
                    <EmptyPlaceholder>
                      {tCommon('noEntitySpecified', {
                        entity: tGlobal('entity.agreementParty', { context: 'plural' }).toLowerCase(),
                      })}
                    </EmptyPlaceholder>
                  </caption>
                )}

                <TableHead>
                  <TableHeadRow>
                    <TableHeadCell className="w-[20%]">{tGlobal('entity.legalPerson')}</TableHeadCell>
                    <TableHeadCell className="w-[15%]">{tGlobal('agreementParty.type')}</TableHeadCell>
                    <TableHeadCell className="w-[20%]">{tAgreements('agreement.contactPerson')}</TableHeadCell>
                    <TableHeadCell className="w-[20%]">{tAgreements('agreement.contactEmail')}</TableHeadCell>
                    <TableHeadCell className="w-[15%]">{tAgreements('agreement.contactPhone')}</TableHeadCell>
                    {editing && !isContactOnlyMode && <TableHeadCell className="w-[10%]" />}
                  </TableHeadRow>
                </TableHead>

                <TableBody className={editing ? 'border-b' : ''}>
                  {isLoading && Array.from(Array(3).keys()).map((x, idx) => <AgreementPartyItem key={x} isFetching />)}

                  {!isLoading && !editing && (
                    <>
                      {ownersManagersParties.map((agreementParty) => (
                        <AgreementPartyItem
                          key={agreementParty.id}
                          agreementParty={agreementParty}
                          isFetching={isFetching}
                        />
                      ))}
                      {tenantParties.map((agreementParty) => (
                        <AgreementPartyItem
                          key={agreementParty.id}
                          agreementParty={agreementParty}
                          isFetching={isFetching}
                        />
                      ))}
                    </>
                  )}

                  {!isLoading && editing && (
                    <>
                      {ownersManagersParties.map((agreementParty) => (
                        <AgreementPartyItemForm
                          key={agreementParty.id}
                          fieldIndex={-1}
                          stakeholder={agreementParty.stakeholder}
                          role={agreementParty.type ?? undefined}
                          isFetching={isFetching}
                          availableContacts={availableContactsMap[agreementParty.id] ?? []}
                          selectedRepresentativeId={representativeMap[agreementParty.id] ?? agreementParty.representative?.id ?? null}
                          onRepresentativeChange={(_idx, repId) => handleRepresentativeChange(agreementParty.id, repId)}
                          contactOnlyMode={isContactOnlyMode}
                        />
                      ))}
                      {editedAgreementParties?.map((field, index) => {
                        const formItem = getFormItemByFieldIndex(index);
                        const partyId = formItem?.id;
                        const stakeholderId = formItem?.stakeholderId;
                        const contactKey = partyId ?? stakeholderId;

                        return (
                          <AgreementPartyItemForm
                            key={field.id}
                            fieldIndex={index}
                            stakeholder={getStakeholderByFieldIndex(index)}
                            role={formItem?.role}
                            isFetching={isFetching}
                            onRemove={isContactOnlyMode ? undefined : handleRemoveLegalPerson}
                            onEdit={isContactOnlyMode ? undefined : handleOpenAddLegalPersons}
                            availableContacts={contactKey ? (availableContactsMap[contactKey] ?? []) : []}
                            selectedRepresentativeId={contactKey ? (representativeMap[contactKey] ?? null) : null}
                            onRepresentativeChange={(_idx, repId) => {
                              if (contactKey) handleRepresentativeChange(contactKey, repId);
                            }}
                            contactOnlyMode={isContactOnlyMode}
                          />
                        );
                      })}

                      {!hasTenant && !isContactOnlyMode && (
                        <TableRow verticalAlign="middle">
                          <TableCell colSpan={6}>
                            <div className="flex items-center justify-center">
                              <Button
                                className="size-9 flex items-center justify-center font-bold text-lg"
                                size="xs"
                                color="primary"
                                disabled={isFetching}
                                onClick={handleOpenAddLegalPersons}
                              >
                                +
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
              {/* {!editing && <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />} */}
            </CardContent>
          </form>
        </FormProvider>
      </Card>

      <CancelRequestModal
        open={cancelingRequest}
        onConfirm={handleConfirmCancelRequest}
        onCancel={handleCancelCancelRequest}
      />

      {editing && !isContactOnlyMode && (
        <LegalPersonsAddModal
          open={addingLegalPersons}
          selected={stakeholders}
          onClose={handleCloseAddLegalPersons}
          onChange={handleAddLegalPersons}
          maxSelectedItems={1}
          maxSelectedItemsMessage={tAgreements('agreement.maxOneTenantPerAgreement')}
        />
      )}
    </>
  );
}
